import { createTheme, alpha, type ThemeOptions } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

export type ColorMode = PaletteMode;

const HEADING_STACK =
  '"Plus Jakarta Sans", "Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const BODY_STACK =
  '"Inter", "Plus Jakarta Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: BODY_STACK,
  htmlFontSize: 16,
  fontSize: 15,
  h1: {
    fontFamily: HEADING_STACK,
    fontWeight: 800,
    fontSize: 'clamp(2.5rem, 5vw + 1rem, 3.75rem)',
    lineHeight: 1.1,
    letterSpacing: '-0.025em',
  },
  h2: {
    fontFamily: HEADING_STACK,
    fontWeight: 800,
    fontSize: 'clamp(2rem, 3.5vw + 0.5rem, 3rem)',
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontFamily: HEADING_STACK,
    fontWeight: 700,
    fontSize: 'clamp(1.75rem, 2.5vw + 0.5rem, 2.5rem)',
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
  },
  h4: {
    fontFamily: HEADING_STACK,
    fontWeight: 700,
    fontSize: '1.875rem',
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontFamily: HEADING_STACK,
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: 1.3,
    letterSpacing: '-0.005em',
  },
  h6: {
    fontFamily: HEADING_STACK,
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.4,
  },
  subtitle1: { fontSize: '1.125rem', lineHeight: 1.6, fontWeight: 500 },
  subtitle2: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 600 },
  body1: { fontSize: '1.0625rem', lineHeight: 1.65 },
  body2: { fontSize: '0.9375rem', lineHeight: 1.6 },
  button: {
    fontFamily: HEADING_STACK,
    fontSize: '0.9375rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: 0,
  },
  caption: { fontSize: '0.8125rem', lineHeight: 1.5 },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
};

const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: '#C8782B',
    light: '#E29A52',
    dark: '#8E5512',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0F766E',
    light: '#14857B',
    dark: '#0B5751',
    contrastText: '#FFFFFF',
  },
  error: { main: '#C62828' },
  warning: { main: '#F57F17' },
  success: { main: '#2E7D32' },
  info: { main: '#1976D2' },
  background: {
    default: '#FAF7F2',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#1A1816',
    secondary: '#5C5852',
    disabled: '#A6A09A',
  },
  divider: 'rgba(26, 24, 22, 0.08)',
  action: {
    hover: 'rgba(200, 120, 43, 0.06)',
    selected: 'rgba(200, 120, 43, 0.12)',
  },
};

const darkPalette = {
  mode: 'dark' as const,
  primary: {
    // Softer, slightly desaturated copper for dark mode. The original
    // #E89B5A reads as candy-bright on a near-black canvas; this muted
    // tone keeps the brand identity without screaming at the user.
    main: '#D89060',
    light: '#E8AC7E',
    dark: '#B57843',
    contrastText: '#1A1108',
  },
  secondary: {
    main: '#2DD4BF',
    light: '#5EEAD4',
    dark: '#14857B',
    contrastText: '#0A1F1D',
  },
  error: { main: '#F47174' },
  warning: { main: '#F5B543' },
  success: { main: '#5BC274' },
  info: { main: '#60A5FA' },
  background: {
    default: '#14110E',
    paper: '#1F1B17',
  },
  text: {
    primary: '#F5F0E8',
    secondary: '#B5ADA1',
    disabled: '#6B6660',
  },
  divider: 'rgba(245, 240, 232, 0.10)',
  action: {
    hover: 'rgba(232, 155, 90, 0.08)',
    selected: 'rgba(232, 155, 90, 0.16)',
  },
};

const buildTheme = (mode: ColorMode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const isDark = mode === 'dark';

  return createTheme({
    palette,
    typography: sharedTypography,
    shape: { borderRadius: 12 },
    shadows: isDark
      ? [
          'none',
          '0 1px 2px rgba(0,0,0,0.4)',
          '0 2px 6px rgba(0,0,0,0.45)',
          '0 4px 12px rgba(0,0,0,0.5)',
          '0 6px 16px rgba(0,0,0,0.5)',
          '0 8px 20px rgba(0,0,0,0.55)',
          '0 10px 24px rgba(0,0,0,0.55)',
          '0 12px 28px rgba(0,0,0,0.55)',
          '0 14px 32px rgba(0,0,0,0.6)',
          '0 16px 36px rgba(0,0,0,0.6)',
          '0 18px 40px rgba(0,0,0,0.6)',
          '0 20px 44px rgba(0,0,0,0.6)',
          '0 22px 48px rgba(0,0,0,0.65)',
          '0 24px 52px rgba(0,0,0,0.65)',
          '0 26px 56px rgba(0,0,0,0.65)',
          '0 28px 60px rgba(0,0,0,0.65)',
          '0 30px 64px rgba(0,0,0,0.7)',
          '0 32px 68px rgba(0,0,0,0.7)',
          '0 34px 72px rgba(0,0,0,0.7)',
          '0 36px 76px rgba(0,0,0,0.7)',
          '0 38px 80px rgba(0,0,0,0.75)',
          '0 40px 84px rgba(0,0,0,0.75)',
          '0 42px 88px rgba(0,0,0,0.75)',
          '0 44px 92px rgba(0,0,0,0.75)',
          '0 46px 96px rgba(0,0,0,0.8)',
        ]
      : [
          'none',
          '0 1px 2px rgba(26,24,22,0.04)',
          '0 2px 6px rgba(26,24,22,0.06)',
          '0 4px 12px rgba(26,24,22,0.07)',
          '0 6px 16px rgba(26,24,22,0.08)',
          '0 8px 20px rgba(26,24,22,0.08)',
          '0 10px 24px rgba(26,24,22,0.09)',
          '0 12px 28px rgba(26,24,22,0.09)',
          '0 14px 32px rgba(26,24,22,0.1)',
          '0 16px 36px rgba(26,24,22,0.1)',
          '0 18px 40px rgba(26,24,22,0.11)',
          '0 20px 44px rgba(26,24,22,0.11)',
          '0 22px 48px rgba(26,24,22,0.12)',
          '0 24px 52px rgba(26,24,22,0.12)',
          '0 26px 56px rgba(26,24,22,0.13)',
          '0 28px 60px rgba(26,24,22,0.13)',
          '0 30px 64px rgba(26,24,22,0.14)',
          '0 32px 68px rgba(26,24,22,0.14)',
          '0 34px 72px rgba(26,24,22,0.15)',
          '0 36px 76px rgba(26,24,22,0.15)',
          '0 38px 80px rgba(26,24,22,0.16)',
          '0 40px 84px rgba(26,24,22,0.16)',
          '0 42px 88px rgba(26,24,22,0.17)',
          '0 44px 92px rgba(26,24,22,0.17)',
          '0 46px 96px rgba(26,24,22,0.18)',
        ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
          body: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
            transition: 'background-color 200ms ease, color 200ms ease',
          },
          '*::selection': {
            backgroundColor: alpha(palette.primary.main, 0.25),
          },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(palette.text.primary, 0.18),
            borderRadius: 8,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(palette.text.primary, 0.3),
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingInline: 18,
            paddingBlock: 9,
          },
          sizeLarge: { paddingInline: 24, paddingBlock: 12, fontSize: '1rem' },
          // Filled primary buttons drop the glow in dark mode — a coloured
          // halo on a near-black canvas reads as "neon" and pulls the eye
          // away from the form. In light mode the soft shadow stays.
          containedPrimary: isDark
            ? {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: palette.primary.dark,
                },
              }
            : {
                boxShadow: `0 6px 16px ${alpha(palette.primary.main, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 8px 22px ${alpha(palette.primary.main, 0.35)}`,
                },
              },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark
              ? '0 1px 2px rgba(0,0,0,0.4)'
              : '0 1px 3px rgba(26,24,22,0.05)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backdropFilter: 'saturate(180%) blur(8px)',
            backgroundColor: alpha(palette.background.paper, isDark ? 0.85 : 0.9),
            color: palette.text.primary,
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: palette.background.paper,
            borderRight: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? alpha(palette.background.paper, 0.6)
              : alpha(palette.primary.main, 0.04),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600, color: palette.text.primary },
          root: { borderBottom: `1px solid ${palette.divider}` },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 8 },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'medium', variant: 'outlined' },
      },
      MuiSelect: {
        defaultProps: { size: 'medium' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark
              ? alpha(palette.background.default, 0.5)
              : palette.background.paper,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(palette.primary.main, 0.5),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontWeight: 500 },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: { fontWeight: 500 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '0.8125rem',
            backgroundColor: isDark ? '#2A241F' : '#1A1816',
            paddingInline: 10,
            paddingBlock: 6,
            borderRadius: 6,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            border: `1px solid ${palette.divider}`,
          },
        },
      },
    },
  });
};

export const getTheme = buildTheme;

export default buildTheme('light');
